// Pure functions for the 16 Tiles print-availability lookup — no DOM access.
// Fetching/DOM wiring lives in truchet-generator.js.

export const PRINTS_SELECT_FIELDS =
    'code,status,print_method,substrate:substrates(name,color),size,ink:inks(name,color),asking_price,notes';

export function buildPrintsEndpoint(baseUrl) {
    const trimmed = String(baseUrl).replace(/\/+$/, '');
    return `${trimmed}/rest/v1/prints?select=${PRINTS_SELECT_FIELDS}`;
}

export function buildPrintMap(rows) {
    const map = new Map();
    if (!Array.isArray(rows)) return map;
    for (const row of rows) {
        if (!row || typeof row.code !== 'string') continue;
        map.set(row.code.toUpperCase(), row);
    }
    return map;
}

export function formatPrice(value) {
    const n = Number(value);
    if (value === null || value === undefined || !Number.isFinite(n)) return null;
    return `€${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function formatMaterial(material) {
    if (!material || !material.name) return null;
    return material.color ? `${material.name} ${material.color}` : material.name;
}

export function buildSpecs(row) {
    const specs = [];
    if (row.print_method) {
        specs.push({ label: 'Method', value: String(row.print_method) });
    }
    const substrate = formatMaterial(row.substrate);
    if (substrate) {
        specs.push({ label: 'Substrate', value: substrate });
    }
    if (row.size) {
        specs.push({ label: 'Size', value: String(row.size) });
    }
    const ink = formatMaterial(row.ink);
    if (ink) {
        specs.push({ label: 'Ink', value: ink });
    }
    if (row.notes) {
        specs.push({ label: 'Notes', value: String(row.notes) });
    }
    return specs;
}

export const STATUS_LABELS = {
    available: 'Available',
    potentially_available: 'On Request',
    unavailable: 'Unavailable',
};

const INQUIRY_EMAIL = 'don@katsudon.press';

export function buildMailtoUrl(code, view) {
    const subject = `16 Tiles print inquiry: ${code}`;
    const priceLine = view.status === 'available' && view.price ? `\n\nListed at ${view.price}.` : '';
    const body = `Hi Don,\n\nI'm interested in the 16 Tiles print with code ${code}.${priceLine}\n\nCould you tell me more about availability?\n\nThanks!`;
    return `mailto:${INQUIRY_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export function getAvailabilityView(map, code) {
    if (typeof code !== 'string') return null;
    const row = map.get(code.toUpperCase());
    if (!row) {
        return {
            status: 'potentially_available',
            badgeLabel: STATUS_LABELS.potentially_available,
            specs: [],
            price: null,
        };
    }

    const view = {
        status: row.status,
        badgeLabel: STATUS_LABELS[row.status] ?? row.status,
        specs: buildSpecs(row),
        price: null,
    };
    if (row.status === 'available') {
        view.price = formatPrice(row.asking_price);
    }
    return view;
}

export function escapeHtml(value) {
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

const CTA_STATUSES = new Set(['available', 'potentially_available']);

export function renderAvailabilityHtml(view, code) {
    const isCta = CTA_STATUSES.has(view.status);
    const badgeClass = `truchet-gen__availability-badge truchet-gen__availability-badge--${escapeHtml(
        view.status
    )}${isCta ? ' truchet-gen__availability-badge--cta' : ''}`;
    const badge = isCta
        ? `<a class="${badgeClass}" href="${escapeHtml(buildMailtoUrl(code, view))}">${escapeHtml(view.badgeLabel)}</a>`
        : `<p class="${badgeClass}">${escapeHtml(view.badgeLabel)}</p>`;

    const specsItems = view.specs
        .map(
            (spec) =>
                `<li><span class="truchet-gen__spec-label">${escapeHtml(
                    spec.label
                )}</span><span class="truchet-gen__spec-value">${escapeHtml(spec.value)}</span></li>`
        )
        .join('');
    const specs = specsItems ? `<ul class="truchet-gen__availability-specs">${specsItems}</ul>` : '';

    const price = view.price ? `<p class="truchet-gen__availability-price">${escapeHtml(view.price)}</p>` : '';

    return `${badge}${specs}${price}`;
}
